import '.a{display:flex;}.ssr-plugin'
import '.d7{align-items:flex-start;}.ssr-plugin'
import '.u{position:absolute;}.ssr-plugin'
import '.gg{top:24px;}.ssr-plugin'
import '.gh{right:24px;}.ssr-plugin'
import '.dg{z-index:1;}.ssr-plugin'
import '.cy{padding:24px;}.ssr-plugin'
import '.i{flex-direction:column;}.ssr-plugin'
import '.ag{justify-content:flex-start;}.ssr-plugin'
import '.bf{gap:24px;}.ssr-plugin'
import '.bj{justify-content:space-between;}.ssr-plugin'
import '.d{align-items:center;}.ssr-plugin'
import '.d2{gap:8px;}.ssr-plugin'
import '.t{height:100%;}.ssr-plugin'
import '.gi{gap:24px;}.ssr-plugin'
import '.eo{flex-direction:row;}.ssr-plugin'
import '.a6{gap:16px;}.ssr-plugin'
import '.gj{justify-content:flex-end;}.ssr-plugin'
import React from 'react'
import { styled, Style } from 'inlines'
import { Text } from '../Text/index.js'
import { colors } from '../../tokens/colors.js'
import { shadows } from '../../utils/shadows.js'
import { radius } from '../../utils/radius.js'
import { Overlay } from '../Overlay/index.js'
import { OverlayTrigger } from '../Overlay/OverlayTrigger.js'
import { OverlayContent } from '../Overlay/OverlayContent.js'
import { IconButton } from '../IconButton/index.js'

type ModalProps = {
  title?: string
  description?: string
  size?: 'small' | 'regular' | 'large'
  children?: React.ReactNode | (() => React.ReactNode)
  open?: boolean
  onClose?: () => void
  onOpen?: () => void
  style?: Style
}

const CloseButton = ({ Overlay }) => {
  return (
    <div className="a d7 u gg gh dg"
    >
      <IconButton icon="close" onClick={() => Overlay.Close()} />
    </div>
  )
}

const Modal = ({
  title,
  description,
  size = 'regular',
  children,
  open = false,
  onClose,
  onOpen,
  style,
}: ModalProps) => {
  let childrenRef = children as React.ReactNode
  if (typeof children === 'function') {
    childrenRef = children()
  }
  const triggerElement = React.Children.toArray(childrenRef).find(
    (child) => React.isValidElement(child) && child.type === OverlayTrigger,
  )
  const contentElement = React.Children.toArray(childrenRef).filter(
    (child) => !React.isValidElement(child) || child.type === OverlayContent,
  )

  const headerElement = React.Children.toArray(childrenRef).find(
    (child) => React.isValidElement(child) && child.type === Header,
  )

  const bodyElement = React.Children.toArray(childrenRef).find(
    (child) => React.isValidElement(child) && child.type === Body,
  )

  const footerElement = React.Children.toArray(childrenRef).find(
    (child) => React.isValidElement(child) && child.type === Footer,
  )

  return (
    <Overlay
      position="center"
      cantCloseOnClickOutside
      open={open}
      onClose={onClose}
      onOpen={onOpen}
    >
      <Overlay.Trigger>{triggerElement}</Overlay.Trigger>
      <Overlay.Content
        style={{
          boxShadow: shadows.popoverLarge,
          borderRadius: radius[20],
        }}
      >
        <styled.div className="cy a i ag bf"
          style={{
            animation:
              size === 'small'
                ? '300ms tilt-strong cubic-bezier(0.30,-0.00,0.40,1.30)'
                : '300ms tilt-soft cubic-bezier(0.30,-0.00,0.60,1.10)',
            height: size === 'large' ? 'calc(100vh - 48px)' : 'undefined',
            width: {
              small: 480,
              regular: 840,
              large: 'calc(100vw - 48px)',
            }[size],
            maxWidth: {
              small: 'none',
              regular: 'none',
              large: '1920px',
            }[size],
            borderRadius: radius[24],
            border: `1px solid ${colors.neutral10}`,
            background: `linear-gradient(${colors.neutral10Background}),
              linear-gradient(${colors.neutralInverted100})`,
            ...style,
          }}
          onClick={(e) => {
            e.stopPropagation()
          }}
        >
          {title && (
            <Text variant="subheading-medium" color="neutral100" style={{}}>
              {title}
            </Text>
          )}
          {description && (
            <Text variant="display-regular" color="neutral60">
              {description}
            </Text>
          )}
          {headerElement ? headerElement : CloseButton({ Overlay })}
          {bodyElement}
          {contentElement}
          {footerElement}
        </styled.div>
      </Overlay.Content>
    </Overlay>
  )
}

type HeaderProps = {
  children?: React.ReactNode
  title?: string
  description?: string
  style?: Style
}

const Header = ({ children, title, description, style }: HeaderProps) => {
  return (
    <>
      <styled.div className="a bj d"
        style={{
          ...style,
        }}
      >
        <div className="a i d2"
        >
          {title && (
            <Text variant="subheading-medium" color="neutral100">
              {title}
            </Text>
          )}
          {description && (
            <Text variant="display-regular" color="neutral60">
              {description}
            </Text>
          )}
        </div>
        <CloseButton Overlay={Overlay} />
      </styled.div>
      {children}
    </>
  )
}

type BodyProps = {
  children?: React.ReactNode
  style?: Style
}

const Body = ({ children, style }: BodyProps) => {
  return (
    <styled.div className="a i t gi"
      style={{
        ...style,
      }}
    >
      {children}
    </styled.div>
  )
}

type FooterProps = {
  children?: React.ReactNode
  style?: Style
}

const Footer = ({ children, style }: FooterProps) => {
  return (
    <styled.div className="a eo a6 gj"
      style={{
        ...style,
      }}
    >
      {children}
    </styled.div>
  )
}

Modal.Trigger = OverlayTrigger
Modal.Content = OverlayContent
Modal.Header = Header
Modal.Body = Body
Modal.Footer = Footer

Modal.Close = Overlay.Close

// Marker for useOverlay hook to identify overlay components in production builds
Modal._isOverlayComponent = true

export { Modal }
